import { IPurchase, PurchaseStatus } from '@snackpass/snackpass-types';
import {
    createEntityAdapter,
    createSlice,
    createSelector,
    createAsyncThunk,
    PayloadAction,
} from '@reduxjs/toolkit';
import { cloneDeep, filter, groupBy, isNull, size } from 'lodash/fp';
import { RootState } from '../store';
import { LoadingStatus } from '../types';
import api from '../../API';
import Constants from '../../Utils/constants';
import {
    normalizedOrderStatus,
    getSecondsLeft,
    getPurchaseStatus,
} from '../../Utils/helpers';
import { IOrderStatus } from '../../Types';
import { last } from 'lodash';
import { getFulfillment } from './screen';

const purchasesAdapter = createEntityAdapter<IPurchase>({
    selectId: (p: IPurchase) => p._id,
});

const combinePurchaseArrays = (res: {
    data: {
        purchases: IPurchase[];
        kioskPurchases?: IPurchase[];
    };
}) => [...res.data.purchases, ...(res.data.kioskPurchases || [])];

export const fetchPurchases = createAsyncThunk(
    'purchases/fetchPurchases',
    async (params?: any) => {
        const response = await api.purchases.get(params);
        const purchases = combinePurchaseArrays(response);
        return purchases;
    }
);

const purchasesSlice = createSlice({
    name: 'purchases',
    initialState: {
        purchases: purchasesAdapter.getInitialState(),
        status: LoadingStatus.Idle,
    },
    reducers: {
        setPurchases(state, action: PayloadAction<IPurchase[]>) {
            purchasesAdapter.setAll(state.purchases, action.payload);
        },
        upsertPurchase(state, action: PayloadAction<IPurchase>) {
            const purchase = state.purchases.entities[action.payload._id];
            const currentLength = purchase?.status?.length ?? 0;

            const uniqueStatus =
                last(purchase?.status)?.type !==
                last(action.payload.status)?.type;
            const newStatus = currentLength < action.payload.status.length;

            if (uniqueStatus && newStatus) {
                purchasesAdapter.upsertOne(state.purchases, action.payload);
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchPurchases.pending, state => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(
                fetchPurchases.fulfilled,
                (state, action: PayloadAction<IPurchase[]>) => {
                    purchasesAdapter.setAll(state.purchases, action.payload);
                    state.status = LoadingStatus.Success;
                }
            )
            .addCase(fetchPurchases.rejected, state => {
                state.status = LoadingStatus.Failed;
            });
    },
});

export const getPurchasesState = (state: RootState) => state.purchases;
export const getAllPurchases = createSelector(
    getPurchasesState,
    state => state.purchases
);

const { selectAll: getPurchases } =
    purchasesAdapter.getSelectors(getAllPurchases);
export const getPurchasesStatus = createSelector(
    getPurchasesState,
    purchases => purchases.status
);

export const getActivePurchases = createSelector(
    [getPurchases, getFulfillment],
    (purchases, fulfillmentFilter) => purchases.filter((purchase: IPurchase) => {
        const secondsLeft = getSecondsLeft(purchase);
        const purchaseInProgress =
            normalizedOrderStatus(purchase) === IOrderStatus.InProgress;
        const purchaseCompletedAndNotExpired =
            normalizedOrderStatus(purchase) === IOrderStatus.Ready &&
            !isNull(secondsLeft) &&
            secondsLeft > Constants.cutoffs.EXPIRED_ORDER * -1;
        const purchaseNotRefunded = !(
            purchase.refund || purchase.partialRefund
        );
        const purchaseNotCanceled =
            getPurchaseStatus(purchase) !== PurchaseStatus.canceled;

        const fulfillmentMatches = !purchase.fulfillment || 
            fulfillmentFilter.includes(purchase.fulfillment);

        return (
            (purchaseInProgress || purchaseCompletedAndNotExpired) &&
            purchaseNotRefunded &&
            purchaseNotCanceled &&
            fulfillmentMatches
        );
    })
);

const statusOrder: { [k in IOrderStatus]: number } = {
    Ready: 1,
    'In Progress': 2,
    'Not Started': 3,
};

export const getActivePurchasesSorted = createSelector(
    getActivePurchases,
    (purchases: IPurchase[]) => {
        const purchasesClone = cloneDeep(purchases);
        return (
            purchasesClone
                // sort by status (Ready, In Progress, Not Started), then order/receipt number
                .sort((a: IPurchase, b: IPurchase) => {
                    return (
                        statusOrder[normalizedOrderStatus(a)] -
                            statusOrder[normalizedOrderStatus(b)] ||
                        a.receiptNumber! - b.receiptNumber! // i.e. Order numer 3 should come before Order number 4
                    );
                })
        );
    }
);

export const getNumTiles = createSelector(
    [getActivePurchasesSorted],
    purchases =>
        purchases.filter(
            p =>
                normalizedOrderStatus(p) === IOrderStatus.Ready ||
                normalizedOrderStatus(p) === IOrderStatus.InProgress
        ).length
);

export const { setPurchases, upsertPurchase } = purchasesSlice.actions;
export const purchasesReducer = purchasesSlice.reducer;
